import { useCallback, useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import clinicAPI from "../../../services/clinicApi";
import ClubLifePostCard from "../common/ClubLifePostCard";
import ClientOverviewSection from "./ClientOverviewSection";
import ClientNotesSection from "./ClientNotesSection";
import {
  ClientMembershipsSection,
  ClientAttendanceSection,
  ClientEventsSection,
} from "./ClientProfileSections";
import AdminGuardiansPanel from "./AdminGuardiansPanel";
import {
  CLIENT_PROFILE_ANCHORS,
  CLIENT_PROFILE_TAB_TO_ANCHOR,
  PATIENT_TAB_ALIASES,
} from "./patientConstants";
import {
  clientProfileSubtitle,
  formatAttendanceSummary,
  formatMembershipDetail,
} from "./clientSportDisplay";
import { useClubLifeInteractions } from "../../../hooks/useClubLifeInteractions";
import { useAdminHubUi } from "../../../hooks/useAdminAppMode";
import { useAdminRefresh } from "../../../hooks/useAdminRefresh";
import useClubPortalBranding from "../../../hooks/useClubPortalBranding";
import {
  mediaUrl,
  TRAINER_COLOR_PRESETS,
} from "../../../utils/clientHeroTheme";
import { HubSegment } from "../AdminPortal/HubPwa";
import { Avatar, Skeleton } from "../../ui";
import ca from "../PatientPortal/clientApp.module.css";
import cs from "../clinic.module.css";

const DEFAULT_TAB = "wall";
const SOCIAL_TABS = new Set(["wall", "achievements"]);

function themeStyle(themeId) {
  const preset = TRAINER_COLOR_PRESETS.find((p) => p.id === themeId);
  if (!preset) return {};
  return {
    "--member-accent": preset.color,
    "--ca-accent": preset.color,
  };
}

function medPreview(edit) {
  const exp = edit?.medical_clearance_expires_at;
  if (!exp) return "не указана";
  const days = Math.ceil((new Date(exp).getTime() - Date.now()) / (86400000));
  if (days < 0) return "просрочена";
  if (days <= 14) return "истекает скоро";
  return "действует";
}

export default function ClientProfileSocialView({
  patientId,
  patient,
  profile,
  edit,
  setEdit,
  sport,
  onSave,
  onArchive,
  onReload,
  writeMessage,
  subdomain,
  newPassword,
  setNewPassword,
  onSavePortalEmail,
  onSetPortalPassword,
  savingPortalEmail,
  settingPortalPassword,
  onAddToCompetition,
}) {
  const [searchParams] = useSearchParams();
  const isApp = useAdminHubUi();
  const { heroStyle } = useClubPortalBranding("aa", null, true);
  const [socialProfile, setSocialProfile] = useState(null);
  const [socialLoading, setSocialLoading] = useState(true);
  const [activeTab, setActiveTab] = useState(DEFAULT_TAB);
  const [wallPosts, setWallPosts] = useState([]);
  const [achievements, setAchievements] = useState([]);
  const [postsLoading, setPostsLoading] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [showGuardians, setShowGuardians] = useState(false);
  const [editingInfo, setEditingInfo] = useState(false);

  const membership = formatMembershipDetail(sport?.membership);
  const attendance = formatAttendanceSummary(
    profile?.attendance_month || sport?.attendance_month,
  );
  const clubCard = socialProfile?.club_card;
  const primaryGroup =
    clubCard?.primary_group ||
    socialProfile?.groups?.[0]?.name ||
    profile?.groups?.[0]?.name ||
    null;

  const shellStyle = useMemo(
    () => themeStyle(socialProfile?.profile_theme),
    [socialProfile?.profile_theme],
  );

  const {
    commentBusy,
    openComments,
    commentsByPost,
    toggleLike,
    loadComments,
    addComment,
  } = useClubLifeInteractions(
    activeTab === "wall" ? setWallPosts : setAchievements,
    { commentsApi: "admin" },
  );

  const loadSocialProfile = useCallback(async () => {
    if (!patientId) return;
    setSocialLoading(true);
    try {
      const data = await clinicAPI.getStaffClientSocialProfile(patientId);
      setSocialProfile(data.profile || null);
    } catch {
      setSocialProfile(null);
    } finally {
      setSocialLoading(false);
    }
  }, [patientId]);

  const loadPosts = useCallback(async () => {
    if (!patientId || !SOCIAL_TABS.has(activeTab)) return;
    setPostsLoading(true);
    try {
      if (activeTab === "wall") {
        const data = await clinicAPI.getStaffClientSocialWall(patientId);
        setWallPosts(data.posts || []);
      } else if (activeTab === "achievements") {
        const data = await clinicAPI.getStaffClientSocialAchievements(patientId);
        setAchievements(data.posts || []);
      }
    } catch {
      if (activeTab === "wall") setWallPosts([]);
      else setAchievements([]);
    } finally {
      setPostsLoading(false);
    }
  }, [activeTab, patientId]);

  useEffect(() => {
    setActiveTab(DEFAULT_TAB);
    loadSocialProfile();
  }, [patientId, loadSocialProfile]);

  useEffect(() => {
    const tab = searchParams.get("tab");
    if (!tab) return;
    const resolved = PATIENT_TAB_ALIASES[tab] || tab;
    const anchor = CLIENT_PROFILE_TAB_TO_ANCHOR[resolved] || resolved;
    if (anchor) setActiveTab(anchor);
  }, [searchParams]);

  useEffect(() => {
    if (!patientId) return;
    if (SOCIAL_TABS.has(activeTab)) loadPosts();
  }, [activeTab, loadPosts, patientId]);

  useAdminRefresh(() => {
    loadSocialProfile();
    onReload?.();
    if (SOCIAL_TABS.has(activeTab)) loadPosts();
  });

  const archived =
    socialProfile?.is_archived || edit?.is_archived || patient?.patient_card?.is_archived;
  const statusLabel = archived
    ? "В архиве"
    : socialProfile?.is_blocked || patient?.is_blocked
      ? "Заблокирован"
      : "Активен";
  const subtitle = clientProfileSubtitle(sport);
  const phone = socialProfile?.phone || patient?.phone;
  const displayName = socialProfile?.full_name || patient?.full_name || "Клиент";
  const coverUrl = mediaUrl(socialProfile?.profile_cover_url);
  const posts = activeTab === "wall" ? wallPosts : achievements;

  const overviewBlock = (
    <div className={cs.staffClientSocialOverview}>
      <ClientOverviewSection
        patientId={patientId}
        patient={patient}
        profile={profile}
        edit={edit}
        setEdit={setEdit}
        onSave={onSave}
        subdomain={subdomain}
        newPassword={newPassword}
        setNewPassword={setNewPassword}
        onSavePortalEmail={onSavePortalEmail}
        onSetPortalPassword={onSetPortalPassword}
        savingPortalEmail={savingPortalEmail}
        settingPortalPassword={settingPortalPassword}
        editingInfo={editingInfo}
        onToggleEdit={() => setEditingInfo((v) => !v)}
        onAddToCompetition={onAddToCompetition}
        social={!isApp}
        compact={isApp}
      />
    </div>
  );

  const tabContent = (
    <>
      {activeTab === "wall" || activeTab === "achievements" ? (
        postsLoading ? (
          <Skeleton height={160} style={{ borderRadius: 16, margin: "12px 0" }} />
        ) : posts.length === 0 ? (
          <div className={ca.memberFeedEmpty}>
            <span>{activeTab === "wall" ? "📝" : "🏆"}</span>
            <p>
              {activeTab === "wall"
                ? "На стене пока нет записей"
                : "Достижений пока нет"}
            </p>
          </div>
        ) : (
          <div className={ca.memberPosts}>
            {posts.map((post) => (
              <ClubLifePostCard
                key={post.id}
                post={post}
                mode="admin"
                onLike={toggleLike}
                onLoadComments={loadComments}
                commentsOpen={openComments === post.id}
                comments={commentsByPost[post.id] || []}
                onComment={addComment}
                commentBusy={commentBusy}
              />
            ))}
          </div>
        )
      ) : null}
      {activeTab === "client-overview" ? overviewBlock : null}
      {activeTab === "client-memberships" ? (
        <ClientMembershipsSection
          patientId={patientId}
          profile={profile}
          onChanged={onReload}
          compact
        />
      ) : null}
      {activeTab === "client-attendance" ? (
        <ClientAttendanceSection patientId={patientId} />
      ) : null}
      {activeTab === "client-events" ? (
        <ClientEventsSection patientId={patientId} profile={profile} />
      ) : null}
      {activeTab === "client-notes" ? (
        <ClientNotesSection
          patientId={patientId}
          legacyNote={patient?.internal_notes}
        />
      ) : null}
    </>
  );

  const tabNav = isApp ? (
    <HubSegment
      tabs={CLIENT_PROFILE_ANCHORS}
      active={activeTab}
      onChange={setActiveTab}
    />
  ) : (
    <nav className={cs.staffClientSocialTabs} aria-label="Разделы профиля">
      {CLIENT_PROFILE_ANCHORS.map((t) => (
        <button
          key={t.id}
          type="button"
          className={
            activeTab === t.id
              ? cs.staffClientSocialTabOn
              : cs.staffClientSocialTab
          }
          onClick={() => setActiveTab(t.id)}
        >
          {t.label}
        </button>
      ))}
    </nav>
  );

  if (socialLoading && !socialProfile) {
    return <Skeleton height={320} style={{ borderRadius: 16 }} />;
  }

  return (
    <div
      className={`${ca.memberProfilePage} ${cs.staffClientSocial}`}
      style={shellStyle}
    >
      <div className={ca.memberCoverWrap}>
        <div className={ca.memberCover}>
          <div
            className={ca.memberCoverBg}
            style={
              coverUrl ? { backgroundImage: `url(${coverUrl})` } : heroStyle
            }
          />
          {coverUrl ? <div className={ca.memberCoverGlow} aria-hidden /> : null}
        </div>
      </div>

      <div className={ca.memberBody}>
        <div className={cs.staffClientSocialHead}>
          <div className={cs.staffClientSocialTopRow}>
            <Avatar
              name={displayName}
              src={mediaUrl(socialProfile?.photo_url || patient?.photo_url)}
              size="lg"
              className={`${ca.memberAvatarIg} ${cs.staffClientSocialAvatar}`}
            />
            <div className={cs.staffClientSocialStats} aria-label="Статистика">
              <div className={cs.staffClientSocialStatItem}>
                <strong>{socialProfile?.wall_posts_count ?? 0}</strong>
                <span>записи</span>
              </div>
              <div className={cs.staffClientSocialStatItem}>
                <strong>{socialProfile?.followers_count ?? 0}</strong>
                <span>подписчики</span>
              </div>
              <div className={cs.staffClientSocialStatItem}>
                <strong>{socialProfile?.following_count ?? 0}</strong>
                <span>подписки</span>
              </div>
            </div>
          </div>

          <div className={cs.staffClientSocialMain}>
            <div className={ca.memberIdentity}>
              <h1 className={ca.memberNameIg}>{displayName}</h1>
              <p className={cs.staffClientSocialMeta}>
                <span
                  className={
                    archived
                      ? cs.staffClientSocialArchived
                      : cs.staffClientSocialActive
                  }
                >
                  {statusLabel}
                </span>
                {subtitle ? <span>{subtitle}</span> : null}
              </p>
              {socialProfile?.profile_bio ? (
                <p className={ca.memberBioIg}>{socialProfile.profile_bio}</p>
              ) : null}
            </div>

            <div className={cs.staffClientSocialActions}>
              {phone ? (
                <a href={`tel:${phone}`} className={cs.staffClientSocialBtnPrimary}>
                  Позвонить
                </a>
              ) : null}
              <button
                type="button"
                className={cs.staffClientSocialBtnGhost}
                onClick={writeMessage}
              >
                Написать
              </button>
              <div className={cs.staffClientSocialMenuWrap}>
                <button
                  type="button"
                  className={cs.staffClientSocialBtnIcon}
                  onClick={() => setMenuOpen((v) => !v)}
                  aria-expanded={menuOpen}
                  aria-label="Ещё действия"
                >
                  ⋯
                </button>
                {menuOpen ? (
                  <div className={cs.staffClientSocialMenu}>
                    <button
                      type="button"
                      onClick={() => {
                        setMenuOpen(false);
                        setShowGuardians((v) => !v);
                      }}
                    >
                      Родители
                    </button>
                    {!archived ? (
                      <button
                        type="button"
                        className={cs.staffClientSocialMenuDanger}
                        onClick={() => {
                          setMenuOpen(false);
                          onArchive();
                        }}
                      >
                        В архив
                      </button>
                    ) : null}
                  </div>
                ) : null}
              </div>
            </div>
          </div>
        </div>

        <div className={cs.staffClientSocialStrip}>
          <button
            type="button"
            className={cs.staffClientSocialStatCard}
            onClick={() => setActiveTab("client-overview")}
          >
            <span className={cs.staffClientSocialStatLabel}>Группа</span>
            <strong>{primaryGroup || "—"}</strong>
          </button>
          <button
            type="button"
            className={cs.staffClientSocialStatCard}
            onClick={() => setActiveTab("client-attendance")}
          >
            <span className={cs.staffClientSocialStatLabel}>Месяц</span>
            <strong>{attendance.label}</strong>
            {attendance.hint ? (
              <small>{attendance.hint}</small>
            ) : null}
          </button>
          <button
            type="button"
            className={cs.staffClientSocialStatCard}
            onClick={() => setActiveTab("client-memberships")}
          >
            <span className={cs.staffClientSocialStatLabel}>Абонемент</span>
            <strong>{clubCard?.membership_label || membership.title}</strong>
            {membership.meta ? <small>{membership.meta}</small> : null}
          </button>
          <button
            type="button"
            className={cs.staffClientSocialStatCard}
            onClick={() => setActiveTab("client-overview")}
          >
            <span className={cs.staffClientSocialStatLabel}>Допуск</span>
            <strong>{medPreview(edit)}</strong>
            <small>мед. справка</small>
          </button>
        </div>

        {showGuardians ? (
          <div className={cs.staffClientSocialGuardians}>
            <AdminGuardiansPanel patientId={patientId} />
          </div>
        ) : null}

        {tabNav}

        <div className={cs.staffClientSocialTabPanel}>{tabContent}</div>
      </div>
    </div>
  );
}
